import { useEffect, useState } from 'react';
import * as Icons from '../icons';
import type { Criterion } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  initial: Criterion[];
  onSave: (c: Criterion[]) => void;
}

export function CriteriaModal({ open, onClose, initial, onSave }: Props) {
  const [items, setItems] = useState<Criterion[]>(initial);
  // Resync when modal opens — `initial` may have changed since last mount.
  useEffect(() => {
    if (open) setItems(initial);
  }, [open, initial]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
    >
      <div
        className="flex w-[min(640px,96vw)] min-w-[480px] max-h-[80vh] flex-col overflow-hidden rounded-lg border border-[var(--border-default)] bg-[var(--surface)] shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex h-[50px] flex-none items-center justify-between border-b border-[var(--border-default)] bg-[var(--surface)] px-4">
          <div className="text-base font-medium">Criteria</div>
          <div className="flex items-center gap-3">
            <a className="inline-flex cursor-pointer items-center gap-1 text-sm text-[var(--text-link)] hover:underline">
              <Icons.GridView style={{ width: 14, height: 14 }} /> Select Preset
            </a>
            <a className="inline-flex cursor-pointer items-center gap-1 text-sm text-[var(--text-link)] hover:underline">
              <Icons.Bookmark style={{ width: 14, height: 14 }} /> Save Preset
            </a>
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]"
              onClick={onClose}
            >
              <Icons.Close />
            </button>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Most Important
          </div>
          {items.map((c, i) => (
            <div
              key={c.id}
              className="mb-2 flex items-center gap-2.5 rounded-md border border-[var(--border-muted)] bg-[var(--surface)] p-2"
            >
              <span className="flex cursor-grab items-center text-[var(--text-tertiary)]">
                <Icons.Drag />
              </span>
              <span className="w-6 text-center text-sm text-[var(--text-muted)]">{i + 1}</span>
              <input
                className="flex-1 border-0 bg-transparent text-sm outline-none"
                value={c.text}
                onChange={e =>
                  setItems(items.map(x => (x.id === c.id ? { ...x, text: e.target.value } : x)))
                }
              />
              <span
                className="flex cursor-pointer items-center text-rose-700"
                onClick={() => setItems(items.filter(x => x.id !== c.id))}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <circle cx="12" cy="12" r="10" fill="#fde7e7" />
                  <path stroke="#d32f2f" strokeWidth="2" d="M9 9l6 6M15 9l-6 6" />
                </svg>
              </span>
            </div>
          ))}
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Least Important
          </div>
          <div className="flex items-center justify-between pt-3">
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-muted)]"
              onClick={() =>
                setItems([...items, { id: 'crit-' + Date.now(), text: '' }])
              }
            >
              <Icons.Plus style={{ width: 14, height: 14 }} /> Add Criterion
            </button>
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-strong)]"
              onClick={() => {
                onSave(items);
                onClose();
              }}
            >
              Update <Icons.ArrowOutward />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
